import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { uniqBy } from 'lodash-es';
export const MAX_CHARACTERS_TO_AUTOSUGGEST = 60;
// report batch of latencies 2 seconds after no suggestion response
export const REPORT_LATENCY_DELAY = 2000;
// keep in sync with pplx.common.core.consts.SUGGESTION_LIMIT
const WEB_SUGGESTION_LIMIT = 6;
const MOBILE_SUGGESTION_LIMIT = 4;
const WEB_SUGGESTIONS_WITH_SHORTCUTS_LIMIT = 5;
export const getSuggestionsLimit = (isMobile, hasShortcuts) => {
    if (isMobile) {
        return MOBILE_SUGGESTION_LIMIT;
    }
    if (hasShortcuts) {
        return WEB_SUGGESTIONS_WITH_SHORTCUTS_LIMIT;
    }
    return WEB_SUGGESTION_LIMIT;
};
export const changeShouldUpdateSuggestions = (input) => {
    if (input.length === 0)
        return false;
    const includesNewline = input.includes('\n');
    if (includesNewline)
        return false;
    return true;
};
/** Suggestion categories whose description is the row label rather than query text. */
function isDescriptionLabelCategory(category) {
    switch (category) {
        case 'ticker':
        case 'news':
            return true;
        default:
            return false;
    }
}
/** Search focuses whose suggestions use the description as the row label. */
function isDescriptionLabelFocus(focus) {
    switch (focus) {
        case 'finance':
        case 'sports':
            return true;
        default:
            return false;
    }
}
/** Keeps ordinary query text visible unless the category or focus uses descriptions as labels. */
export function getDisplayedSuggestionDescription({ description, category, focus, }) {
    return isDescriptionLabelCategory(category) || isDescriptionLabelFocus(focus)
        ? description
        : undefined;
}
/** Keeps empty descriptions on the same title/query fallback as absent descriptions. */
export const getSuggestionDisplayText = ({ description, shouldShowTitle, title, query, }) => description || (shouldShowTitle && title ? title : query);
/** A row's explicit action takes precedence over its normal pointer submission. */
export const runSuggestionAction = (actionHandler, submit) => {
    if (actionHandler) {
        actionHandler();
        return;
    }
    submit();
};
const PERPLEXITY_FINANCE_HOSTNAMES = new Set(['perplexity.ai', 'www.perplexity.ai']);
const getUrlPathname = (url) => {
    if (url.startsWith('/')) {
        return url.split(/[?#]/u)[0] ?? null;
    }
    try {
        const parsedUrl = new URL(url);
        return PERPLEXITY_FINANCE_HOSTNAMES.has(parsedUrl.hostname) ? parsedUrl.pathname : null;
    }
    catch {
        return null;
    }
};
const isFinancePath = (pathname) => {
    return pathname === '/finance' || pathname.startsWith('/finance/');
};
export const normalizeFinanceSuggestionUrl = (url) => {
    if (!url) {
        return url;
    }
    try {
        const parsedUrl = new URL(url);
        if (!PERPLEXITY_FINANCE_HOSTNAMES.has(parsedUrl.hostname) ||
            !isFinancePath(parsedUrl.pathname)) {
            return url;
        }
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }
    catch {
        return url;
    }
};
export const parseSuggestions = (unparsedSuggestions) => {
    return unparsedSuggestions.map((suggestion) => ({
        query: suggestion['text'],
        image: suggestion['image_url'],
        url: normalizeFinanceSuggestionUrl(suggestion['url']),
        title: suggestion['title'],
        description: suggestion['description'],
        focus: suggestion['focus'],
        submission_query: suggestion['submission_query'],
    }));
};
const sortSuggestions = (suggestions) => {
    // Sorts in place mutating the original array
    return suggestions.sort((a, b) => {
        if (a.url)
            return -1;
        if (b.url)
            return 1;
        if (a.image)
            return -1;
        if (b.image)
            return 1;
        return 0;
    });
};
export const filterSortAndDedupeSuggestions = (fetchedSuggestions, isMobile, query) => {
    let filteredFetchedSuggestions = fetchedSuggestions;
    // 1. remove navigational suggestions on mobile or sidecar
    // mweb blocks window.open popups + we don't want non-pplx tabs in sidecar
    if (isMobile || !!import.meta.env.VITE_IS_SIDECAR_BUILD) {
        filteredFetchedSuggestions = fetchedSuggestions.filter((s) => !s.url);
    }
    // 2. dedupe according to query
    const dedupedSuggestions = uniqBy(filteredFetchedSuggestions, 'query');
    // 3. optionally move exact match to the front
    if (query) {
        const exactMatchIndex = dedupedSuggestions.findIndex((suggestion) => suggestion.query === query);
        if (~exactMatchIndex) {
            const exactMatch = dedupedSuggestions[exactMatchIndex];
            dedupedSuggestions.splice(exactMatchIndex, 1);
            dedupedSuggestions.unshift(exactMatch);
        }
    }
    // 4. sort prioritizing navigational, then images
    const sortedSuggestions = sortSuggestions(dedupedSuggestions);
    return [sortedSuggestions, sortedSuggestions.slice(0, getSuggestionsLimit(isMobile))];
};
/**
 * Returns true if all characters in the query value are included in the Suggesion query,
 * ignoring whitespace and case, but not necessarily in sequential order one after another.
 * @param queryValue - The query value to check.
 * @param suggestion - The Suggestion to check against.
 * @param compress - Whether to compress the query value (remove white space and make
 * locale lowercase). If iterating over a large list of Suggestions, it's more efficient
 * to pre-compress the query value than to do it for each invocation.
 * @returns [boolean, number] - True|False if match, and the depth of query value into
 * Suggestion query for match to occur.
 */
export const isNonSequentialMatch = (queryValue, suggestion, compress) => {
    const qv = compress ? queryValue.replace(/\s+/g, '').toLocaleLowerCase() : queryValue;
    const q = suggestion.query;
    let qvIndex = 0;
    let qIndex = 0;
    while (qvIndex < qv.length && qIndex < q.length) {
        if (qv[qvIndex] === q[qIndex]?.toLocaleLowerCase()) {
            qvIndex++;
        }
        qIndex++;
    }
    return [qvIndex === qv.length, qIndex];
};
/**
 * Split a full string into segments based on matching characters from the input, case-insensitive
 * matching preserving the case of the full string in the result.
 * For matching characters, the first string gets them. For non-matching, the second string gets them.
 * Example:
 *   splitIntersectingStrings("testing", "test in the morning")
 *   Returns: [
 *     ["test", ""],     // matching "test"
 *     ["", " "],        // non-matching space
 *     ["in", ""],       // matching "in"
 *     ["", " the mor"], // non-matching " the mor"
 *     ["g", ""],        // matching "g"
 *     ["", "ning"]      // remaining characters
 *   ]
 */
export const splitIntoInputsAndCompletions = (input, full) => {
    const compressedInput = input.replace(/\s+/g, '');
    const result = [];
    let currentMatch = '';
    let currentNonMatch = '';
    let inputIndex = 0;
    let fullIndex = 0;
    while (inputIndex < compressedInput.length && fullIndex < full.length) {
        if (compressedInput[inputIndex]?.toLocaleLowerCase() === full[fullIndex]?.toLocaleLowerCase()) {
            // If we have accumulated any non-matching characters, add them as a segment
            if (currentNonMatch) {
                result.push(['', currentNonMatch]);
                currentNonMatch = '';
            }
            // Add the matching character to the current match using the full string since
            // it will have the correct case
            currentMatch += full[fullIndex];
            inputIndex++;
            fullIndex++;
        }
        else {
            // If we have accumulated any matching characters, add them as a segment
            if (currentMatch) {
                result.push([currentMatch, '']);
                currentMatch = '';
            }
            currentNonMatch += full[fullIndex];
            fullIndex++;
        }
    }
    // Add any final segments
    if (currentMatch) {
        result.push([currentMatch, '']);
    }
    if (currentNonMatch) {
        result.push(['', currentNonMatch]);
    }
    // Add any remaining characters from the full string
    if (fullIndex < full.length) {
        result.push(['', full.slice(fullIndex)]);
    }
    return result;
};
export const ICON_NAME_TO_ICON_MAP = {
    calendar: $icon('calendar'),
    'report-search': $icon('report-search'),
    browser: $icon('browser'),
    mail: $icon('mail'),
    'shopping-bag': $icon('shopping-bag'),
    message: $icon('message'),
    'user-heart': $icon('user-heart'),
    play: $icon('player-play'),
    globe: $icon('globe'),
    music: $icon('music'),
    book: $icon('book'),
    plane: $icon('plane'),
    plus: $icon('plus'),
};
export const isPerplexityUrl = (url) => {
    return isFinanceUrl(url) || (url?.includes('perplexity.ai') ?? false);
};
export const isFinanceUrl = (url) => {
    if (!url) {
        return false;
    }
    const pathname = getUrlPathname(url);
    return pathname === null ? false : isFinancePath(pathname);
};
export const isFinanceRootUrl = (url) => {
    if (!url) {
        return false;
    }
    return getUrlPathname(url) === '/finance';
};
//# sourceMappingURL=utils.js.map